/**
 * "Importar histórico" (`D201`): pick a Strong or Hevy CSV, review how each
 * exercise name was matched, import in batches.
 *
 * ── PRESENTATION OVER INJECTED DEPENDENCIES ────────────────────────────────
 * {@link ImportFlow} takes the catalogue's names, the display name of an id
 * and the batch `send` as props, so the stories drive the whole flow — a
 * real file, a real review, the batches it sends — over a fake. The editor
 * (`history-editors.tsx`) wires the device's catalogue copy and the route.
 *
 * ── A STOPPED IMPORT IS RESUMED, NOT RESTARTED ─────────────────────────────
 * Batches go one at a time, oldest first. A failure stops the loop at that
 * batch and "Tentar de novo" continues from it; a batch the server had in
 * fact written answers `created: false` for every session, so even resending
 * one is harmless — the session ids are derived from the source's own keys
 * (`workout-import.ts`).
 */
import type {
  ImportBatchInput,
  ImportBatchOutput,
} from "@repo/contract/training";
import {
  "history-transfer.choice_label" as choiceLabel,
  "history-transfer.done_body" as doneBody,
  "history-transfer.done" as doneLabel,
  "history-transfer.done_skipped" as doneSkipped,
  "history-transfer.done_title" as doneTitle,
  "history-transfer.empty_file" as emptyFile,
  "history-transfer.failed" as failedText,
  "history-transfer.found" as foundText,
  "history-transfer.importing" as importingText,
  "history-transfer.loading_catalogue" as loadingCatalogue,
  "history-transfer.matched_count" as matchedCount,
  "history-transfer.pick_file" as pickFile,
  "history-transfer.pick_helper" as pickHelper,
  "history-transfer.retry" as retryLabel,
  "history-transfer.review_helper" as reviewHelper,
  "history-transfer.skip_option" as skipOption,
  "history-transfer.start" as startLabel,
  "history-transfer.units_imperial" as unitsImperial,
  "history-transfer.units_label" as unitsLabel,
  "history-transfer.units_metric" as unitsMetric,
  "history-transfer.unknown_file" as unknownFile,
} from "@repo/i18n";
import { FileUp } from "lucide-react";
import {
  type ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Segmented } from "../../../ui/controls";
import { FieldError, Helper } from "../editors/parts";
import { buildImport, distinctNames } from "./build";
import { parseCsv } from "./csv";
import {
  autoChoice,
  type CatalogueName,
  type MatchCandidate,
  rankMatches,
} from "./match";
import {
  detectSource,
  readHevy,
  readStrong,
  type SourceApp,
  type SourceWorkout,
  type StrongUnits,
} from "./sources";

export interface ImportFlowProps {
  /** Every catalogue name to match against — English and the app's own;
   *  `undefined` while the device's copy is loading. */
  readonly catalogue: readonly CatalogueName[] | undefined;
  /** The name to show for a catalogue id, in the app's language. */
  readonly displayName: (exerciseId: string) => string;
  /** The clock, for "a workout dated tomorrow is a wrong clock". */
  readonly now: () => Date;
  /** "Pronto" after the import, and the editor's close. */
  readonly onDone: (summary: { readonly created: number }) => void;
  readonly send: (batch: ImportBatchInput) => Promise<ImportBatchOutput>;
}

interface Parsed {
  readonly rows: readonly (readonly string[])[];
  readonly source: SourceApp;
}

type Progress =
  | { readonly kind: "idle" }
  | { readonly kind: "sending"; readonly done: number }
  | { readonly kind: "failed"; readonly done: number }
  | { readonly kind: "done" };

function readRows(parsed: Parsed, units: StrongUnits): SourceWorkout[] {
  return parsed.source === "strong"
    ? readStrong(parsed.rows, units)
    : readHevy(parsed.rows);
}

function NameChoice(props: {
  readonly candidates: readonly MatchCandidate[];
  readonly displayName: (exerciseId: string) => string;
  readonly name: string;
  readonly onChoose: (name: string, exerciseId: string | null) => void;
  readonly value: string | null;
}) {
  const { name, onChoose } = props;
  const change = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) =>
      onChoose(name, event.target.value === "" ? null : event.target.value),
    [name, onChoose]
  );
  return (
    <li className="border-line border-t py-3 first:border-t-0">
      <p className="font-medium text-base text-ink leading-snug">{name}</p>
      <select
        aria-label={choiceLabel({ name })}
        className="mt-2 h-12 w-full rounded-lg bg-surface-2 px-3 text-base text-ink"
        data-choice={name}
        onChange={change}
        value={props.value ?? ""}
      >
        <option value="">{skipOption()}</option>
        {props.candidates.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {props.displayName(candidate.id)}
          </option>
        ))}
      </select>
    </li>
  );
}

export function ImportFlow(props: ImportFlowProps) {
  const { catalogue, displayName, now, onDone, send } = props;
  const input = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [units, setUnits] = useState<StrongUnits>("metric");
  const [overrides, setOverrides] = useState<
    ReadonlyMap<string, string | null>
  >(() => new Map());
  const [progress, setProgress] = useState<Progress>({ kind: "idle" });
  const [created, setCreated] = useState(0);
  const [existing, setExisting] = useState(0);

  const workouts = useMemo(
    () => (parsed === null ? [] : readRows(parsed, units)),
    [parsed, units]
  );
  const names = useMemo(() => distinctNames(workouts), [workouts]);
  const candidates = useMemo(() => {
    const ranked = new Map<string, MatchCandidate[]>();
    if (catalogue !== undefined) {
      for (const name of names) {
        ranked.set(name, rankMatches(name, catalogue));
      }
    }
    return ranked;
  }, [catalogue, names]);
  const choices = useMemo(() => {
    const chosen = new Map<string, string | null>();
    for (const name of names) {
      chosen.set(
        name,
        overrides.has(name)
          ? (overrides.get(name) ?? null)
          : autoChoice(candidates.get(name) ?? [])
      );
    }
    return chosen;
  }, [candidates, names, overrides]);
  const plan = useMemo(
    () =>
      parsed === null
        ? null
        : buildImport({ choices, now: now(), source: parsed.source, workouts }),
    [choices, now, parsed, workouts]
  );

  const choose = useCallback((name: string, exerciseId: string | null) => {
    setOverrides((was) => new Map(was).set(name, exerciseId));
  }, []);
  const pick = useCallback(() => input.current?.click(), []);
  const read = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file === undefined) {
      return;
    }
    file.text().then(
      (text) => {
        const rows = parseCsv(text);
        const source = detectSource(rows[0] ?? []);
        setOverrides(new Map());
        setProgress({ kind: "idle" });
        if (source === null) {
          setParsed(null);
          setFileError(unknownFile());
          return;
        }
        setFileError(rows.length < 2 ? emptyFile() : null);
        setParsed(rows.length < 2 ? null : { rows, source });
      },
      () => setFileError(unknownFile())
    );
  }, []);

  const run = useCallback(
    async (from: number) => {
      if (plan === null) {
        return;
      }
      for (let index = from; index < plan.batches.length; index += 1) {
        setProgress({ done: index, kind: "sending" });
        const batch = plan.batches[index] as ImportBatchInput;
        let answer: ImportBatchOutput;
        try {
          answer = await send(batch);
        } catch {
          setProgress({ done: index, kind: "failed" });
          return;
        }
        const fresh = answer.sessions.filter((one) => one.created).length;
        setCreated((was) => was + fresh);
        setExisting((was) => was + answer.sessions.length - fresh);
      }
      setProgress({ kind: "done" });
    },
    [plan, send]
  );
  const start = useCallback(() => {
    run(0).catch(() => undefined);
  }, [run]);
  const resume = useCallback(() => {
    if (progress.kind === "failed") {
      run(progress.done).catch(() => undefined);
    }
  }, [progress, run]);
  const finish = useCallback(() => onDone({ created }), [created, onDone]);

  if (progress.kind === "done" && plan !== null) {
    return (
      <div data-testid="import-done">
        <p className="a-display text-ink text-xl">{doneTitle()}</p>
        <p className="mt-2 text-base text-ink-2">
          {doneBody({ created, existing })}
        </p>
        {plan.skippedSets > 0 ? (
          <p className="mt-2 text-ink-3 text-sm">
            {doneSkipped({ sets: plan.skippedSets })}
          </p>
        ) : null}
        <Button className="mt-5 w-full" onClick={finish} size="lg">
          {doneLabel()}
        </Button>
      </div>
    );
  }

  const busy = progress.kind === "sending";
  const matched = [...choices.values()].filter((id) => id !== null).length;
  return (
    <div data-testid="import-flow">
      <Helper>{pickHelper()}</Helper>
      <input
        accept=".csv,text/csv,text/comma-separated-values"
        className="sr-only"
        data-testid="import-file"
        onChange={read}
        ref={input}
        tabIndex={-1}
        type="file"
      />
      <Button
        className="w-full"
        disabled={busy}
        icon={FileUp}
        onClick={pick}
        size="lg"
        variant="secondary"
      >
        {pickFile()}
      </Button>
      {fileError === null ? null : <FieldError>{fileError}</FieldError>}
      {parsed === null ? null : (
        <div className="mt-5">
          <p
            className="font-semibold text-base text-ink"
            data-testid="import-found"
          >
            {foundText({ names: names.length, workouts: workouts.length })}
          </p>
          {parsed.source === "strong" ? (
            <div className="mt-3">
              <p className="mb-2 text-ink-2 text-sm">{unitsLabel()}</p>
              <Segmented
                label={unitsLabel()}
                onValueChange={setUnits}
                options={[
                  { label: unitsMetric(), value: "metric" },
                  { label: unitsImperial(), value: "imperial" },
                ]}
                value={units}
              />
            </div>
          ) : null}
          {catalogue === undefined ? (
            <p aria-busy="true" className="mt-4 text-base text-ink-3">
              {loadingCatalogue()}
            </p>
          ) : (
            <>
              <Helper>{reviewHelper()}</Helper>
              <p className="text-ink-2 text-sm" data-testid="import-matched">
                {matchedCount({ matched, total: names.length })}
              </p>
              <ul className="mt-2">
                {names.map((name) => (
                  <NameChoice
                    candidates={candidates.get(name) ?? []}
                    displayName={displayName}
                    key={name}
                    name={name}
                    onChoose={choose}
                    value={choices.get(name) ?? null}
                  />
                ))}
              </ul>
            </>
          )}
          {progress.kind === "failed" ? (
            <div className="mt-4">
              <FieldError>{failedText()}</FieldError>
              <Button className="mt-3 w-full" onClick={resume} size="lg">
                {retryLabel()}
              </Button>
            </div>
          ) : (
            <Button
              className="mt-5 w-full"
              data-testid="import-start"
              disabled={
                busy || catalogue === undefined || (plan?.workouts ?? 0) === 0
              }
              onClick={start}
              size="lg"
            >
              {busy
                ? importingText({
                    done: progress.done,
                    total: plan?.batches.length ?? 0,
                  })
                : startLabel()}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
